import { contentChild, DestroyRef, Directive, effect, ElementRef, EmbeddedViewRef, HostListener, inject, Injectable, Injector, input, model, NgZone, output, signal, TemplateRef, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { delay, filter, fromEvent, map, race, Subscription, switchMap, take, takeUntil, tap, timer } from 'rxjs';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { logger } from './logger';
import { TrgDragDropService } from './TrgDragDrop';
import { TrgDrop, TrgDropDirective } from './TrgDrop.directive';

const LOGGER = logger('trgDrag');

@Directive({
  selector: '[trgDrag]',
  host: {
    '[class.trg-drag]': '!dragDisabled()',
    '[class.trg-drag-dragging]': 'dragging()',
  },
  exportAs: 'TrgDragDirective',
  standalone: false,
})
export class TrgDragDirective {
  readonly destroyRef = inject(DestroyRef);
  readonly dragDrop = inject(TrgDragDropService);
  readonly injector = inject(Injector);
  readonly viewContainer = inject(ViewContainerRef);
  readonly ngZone = inject(NgZone);
  

  readonly dragStart = output<TrgDrag.DragEvent>({ alias: 'trgDragStart' });
  readonly dragMove = output<TrgDrag.DragEvent>({ alias: 'trgDragMove' });
  readonly dragEnd = output<TrgDrag.DragEvent>({ alias: 'trgDragEnd' });

  /** 
   * When true, drag cannot be dragged.
   * If already dragging, will interrupt and cancel it.
   */
  readonly dragDisabled = input(false);
  
  /**
   * Whether this element is being dragged.
   **/
  readonly dragging = model(false);

  /**
   * One or more tags that link this drag to drops.
   * A drag must have at least one tag that is within set of drop's tags to be accepted by it.
   * If null, this drag will only be accepted by drops with null tags.
   **/
  readonly dropTags = input<string | string[]>(null);

  /**
   * Emitted when a trgDropDirective is entered by this drag.
    */
  readonly enterDrop = output<TrgDrop.DropEvent>({ alias: 'trgDragEnterDrop' });
  
  /**
   * Emitted when a trgDropDirective is exited by this drag.
    */
  readonly exitDrop = output<TrgDrop.DropEvent>({ alias: 'trgDragExitDrop' });

  /**
   * Can this drag be dropped?
   * When false, the drag can be dragged but dropped.
   * When released from drag, it will not emit any drop event
   * for a trgDropDirective.
   **/
  readonly canDrop = input(false);

  /**
   * Predicate to check if this drag can be dropped into a specific trgDropDirective. 
   * This is called after default checks (e.g. tags).
   **/
  readonly dropAllowed = input<TrgDrag.DropPredicate>(null);

  /**
   * Array of mouse buttons that can trigger dragging.
   * Defaults to mouse primary/left click. 
   **/
  readonly buttons = input<ReadonlyArray<TrgDrag.Buttons>>([TrgDrag.Buttons.Primary]);
  
  /**
   * Generic callback for any event that occurs for the drag.
   * This fires before default handelers and can be used to prevent the events from firing. 
   **/
  readonly eventFilter = input<TrgDrag.EventFilter>(null);

  /**
   * Millisecond delay before starting the drag once used clicks on the element.
   **/
  readonly dragDelay = input(0);

  /**
   * Custom data available with this drag.
   * It can be accessed via the drag itself.
   **/
  readonly data = input<any>(null, { alias: 'dragData' });
  
  /**
   * Which axes can the drag move on?
   **/
  readonly dragAxis = input<'x' | 'y' | 'xy'>('xy', { alias: 'trgDragAxis' });
  
  /** 
   * Template that is rendered as a preview for the dragged element.
   * It receives the following context: `{
   *   drag: TrgDragDirective,
   *   dragData: Signal<any>,
   * }`
   * `dragData` is the `data` signal of TrgDragDirective
   **/
  readonly dragPreview = input.required<TemplateRef<any>>();

  /**
   * Defines the size of the preview.
   * This can be a size value, e.g. {width, height} (number or css unit).
   * Or a callback that returns a size value,
   * Or an element of which to use size of.
   * When unassigned, defaults to size of ths drag directive itself.
   * By default, width and height take the `auto` css value.
   **/
  readonly dragPreviewSize = input<TrgDrag.DragPreviewSizeInput>({ width: 'auto', height: 'auto' });
  
  /**
   * Class list applied to the preview element.
   **/
  readonly dragPreviewClass = input<string>('');

  /**
   * When true, dragging will only be allowed by dragging from 
   * a descendant element with the trgDragHandle directive.
   * Otherwise, dragging can start from the trgDragDirective element itself. */
  readonly useDragHandle = input(false);
  

  readonly element = inject<ElementRef<HTMLElement>>(ElementRef);

  private _preview: TrgDrag.DragPreview;
  private _previews = new Set<TrgDrag.DragPreview>();
  private _previewClass = '';

  private _destroyed = false;
  get destroyed() { return this._destroyed; }

  get preview() {
    this.ensurePreview();
    return this._preview;
  }

  _previewDestroyed(preview: TrgDrag.DragPreview) {
    this._previews.delete(preview);

    if (this._preview == preview)
      this._preview = null;
  }

  private ensurePreview() {
    if (this._preview)
      return;

    let element = document.createElement('div');
    element.style.setProperty('display', 'none');
    element.classList.add('trg-drag-preview');

    let preview = new TrgDrag.DragPreview(this, element);
    this._previews.add(preview);
    this._preview = preview;

    document.body.appendChild(element);

    this.updatePreviewSize();
    this.updatePreviewClass();
    this.updatePreviewView();
  }

  private destroyPreviews() {
    let previews = Array.from(this._previews);
    this._previews.clear();

    for (let p of previews) {
      try {
        p.destroy();
      } catch (err) {
        LOGGER.error(`Failed to destory preview`, err);
      }
    }
  }

  private updatePreviewView() {
    let preview = this.preview;

    let template = this.dragPreview();
    if (!template) {
      preview.element.replaceChildren();
      return;
    }

    let v = this.viewContainer.createEmbeddedView(template, { drag: this, dragData: this.data });
    preview._view = v;
    preview.element.replaceChildren(...v.rootNodes);
  }

  private updatePreviewSize() {
    let w, h;

    let size = this.dragPreviewSize();
    if (isNullOrUndefined(size)) {
      size = this.element?.nativeElement;
    }

    if (isNullOrUndefined(size)) {
      size = { width: 0, height: 0 };
      LOGGER.error(`No size provided for preview`);
    }

    if (size instanceof HTMLElement) {
      let rect = size.getBoundingClientRect();
      w = `${rect.width}px`;
      h = `${rect.height}px`;
    }
    else {
      if (typeof size === 'function')
        size = size();

      w = typeof size.width === 'string' ? size.width : `${size.width}px`;
      h = typeof size.height === 'string' ? size.height : `${size.height}px`;
    }

    let element = this.preview.element;
    element.style.setProperty('width', w);
    element.style.setProperty('height', h);
  }

  private updatePreviewClass() {
    let element = this.preview.element;

    let oldClass = this._previewClass;
    if (oldClass.length) 
      element.classList.remove(oldClass);

    let newClass = this.dragPreviewClass()?.trim()
    if (newClass.length) 
      element.classList.add(newClass);

    this._previewClass = newClass;
  }

  private readonly __updatePreviewView = effect(() => {
    // Ensure the effect triggers on change to these
    // We don't actually do anything until preview is available tho
    this.dragPreview();
    this.dragPreviewSize();

    if (!this._preview)
      return;

    this.updatePreviewView();
    this.updatePreviewSize();
    this.updatePreviewClass();
  });


  private _start  = {x: 0, y: 0};
  private _offset = {x: 0, y: 0};
  private _last   = {x: 0, y: 0};

  private _pointerId = null;
  private _drop: TrgDropDirective;

  private _subs: Subscription;

  constructor() {
  }

  ngOnDestroy() {
    this._destroyed = true;
    this._subs?.unsubscribe();
    this.dragDrop.drag.update(x => x === this ? null : x);
    this.tryRestorePointer();
    this.destroyPreviews();
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(mouseEvent: MouseEvent, fromHandle?: boolean) {
    if (this.useDragHandle() && !fromHandle) 
      return;

    if (!this.canDrag(true))
      return;

    let buttons = this.buttons();
    if (buttons && !buttons.includes(mouseEvent.button))
      return false;

    let delay = this.dragDelay();
    if (delay) {
      let button = mouseEvent.button;

      let untilMouseUp =
        fromEvent(window, 'mouseup').pipe(
          filter((e: MouseEvent) => e.button == button),
          take(1)
        );

      timer(delay).pipe(
        takeUntil(untilMouseUp),
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.startDrag(mouseEvent))
      ).subscribe();
    }
    else {
      this.startDrag(mouseEvent);
    }
  }

  private startDrag(mouseEvent: MouseEvent) {
    if (!this.canDrag(true))
      return;

    this._start = {
      x: mouseEvent.pageX,
      y: mouseEvent.pageY
    };

    this._last = { ...this._start };
    
    let elementRect = this.element.nativeElement.getBoundingClientRect();

    this._offset = {
      x: elementRect.x - this._start.x,
      y: elementRect.y - this._start.y
    };

    let startEvent = this.createDragEvent(mouseEvent);
    if (!this.eventAllowed(startEvent))
      return;

    startEvent = {
      ...startEvent,
      preview: this.preview
    };

    this.onDragStart(startEvent);
  }

  private onDragStart(e: TrgDrag.DragEvent) {
    if (!this.canDrag(true)) {
      this.onDragEnd(e);
      return;
    }
      
    e.mouseEvent.preventDefault();

    this._subs?.unsubscribe();
    this._subs = new Subscription();
    this.hookEvent(window, 'mousemove', e => this.onDragMove(e));
    this.hookEvent(window, 'mouseup', e => this.onDragEnd(e));
    
    this.elementOnDragStart(e);
    this.tryCapturePointer(e);

    this.ngZone.run(() => {
      this.dragging.set(true);
      this.dragStart.emit(e);
      this.dragDrop.drag.set(this);
    });

    this.tryDrop(e, false);
  }

  private onDragMove(e: TrgDrag.DragEvent) {
    if (!this.dragging())
     return;

    if (!this.canDrag(false)) {
      this.onDragEnd(e);
      return;
    }
      
    e.mouseEvent.preventDefault();
    
    this.ngZone.run(() => {
      this.dragMove.emit(e);
    });

    this.elementOnDragMove(e);
    this.tryDrop(e, false);
  }

  private onDragEnd(e: TrgDrag.DragEvent) {
    if (!this.dragging())
     return;

    e.mouseEvent.preventDefault();

    try {
      this.tryDrop(e, true);
      this.elementOnDragEnd(e);
    }
    finally {
      try {
        this.tryRestorePointer();

        this.ngZone.run(() => {
          this.dragDrop.drag.update(x => x === this ? null : x);
          this.dragEnd.emit(e);
          this.dragging.set(false);
        });
      }
      finally {
        let preview = this._preview;
        if (preview) {
          if (!preview.keep) {
            preview.destroy();
          }
        }
      }
    }
  }
  
  private elementOnDragStart(e: TrgDrag.DragEvent) {
    let element = this.preview.element;

    element.classList.add('trg-drag-preview-dragging');
    element.style.setProperty('will-change', 'left, top');
    element.style.setProperty('z-index', '2147483647');
    element.style.setProperty('position', 'absolute');
    element.style.setProperty('display', 'block');

    this.elementOnDragMove(e);
  }

  private elementOnDragEnd(e: TrgDrag.DragEvent) {
    let preview = this.preview;
    let element = preview.element;

    if (!preview.keep) {
      element.style.setProperty('display', 'none');
      element.style.setProperty('will-change', null);
      element.style.setProperty('left', null);
      element.style.setProperty('top', null);
      element.style.setProperty('position', null);
      element.style.setProperty('z-index', null);
    }

    element.classList.remove('trg-drag-preview-dragging');
  }

  private elementOnDragMove(e: TrgDrag.DragEvent) {
    let element = this.preview.element;

    let x = this.dragAxis().includes('x') ? Math.round(e.elementX) : (this._start.x + this._offset.x);
    let y = this.dragAxis().includes('y') ? Math.round(e.elementY) : (this._start.y + this._offset.y);

    element.style.setProperty('left', `${x}px`, 'important');
    element.style.setProperty('top',  `${y}px`, 'important');
  }
  
  private tryCapturePointer(e: TrgDrag.DragEvent) {
    try {
      let pointerEvent = <PointerEvent>e.mouseEvent;
      if (pointerEvent.pointerId) {
        this._pointerId = pointerEvent.pointerId;
        this.preview.element.setPointerCapture(pointerEvent.pointerId);
      }
    }
    catch (err) {
      LOGGER.error(`Failed to capture pointer.`, err);
    }
  }

  private tryRestorePointer() {
    try {
      if (this._pointerId) {
        this._preview?.element.releasePointerCapture(this._pointerId);
        this._pointerId = null;
      }
    }
    catch (err) {
      LOGGER.error(`Failed to restore pointer.`, err);
    }
  }

  private tryDrop(e: TrgDrag.DragEvent, dropping: boolean) {
    let drop: TrgDropDirective;

    try {
      if (!this.canDrop())
        return false;
        
      drop = this.dragDrop.getDrop(e);
      if (!drop)
        return false;

      let predicate = this.dropAllowed();
      if (predicate && !predicate(e, drop)) {
        drop = null;
        return false;
      }
     
      if (drop !== this._drop) {
        this.tryExitDrop(e);
        if (!this.tryEnterDrop(e, drop)) {
          drop = null;
          return false;
        }
      }
      
      let element = this.preview.element;
      element.classList.add('trg-drag-preview-dropping');

      if (dropping) {
        element.classList.add('trg-drag-preview-dropped');
        drop.onDrop(e);
      }

      return true;
    }
    finally {
      if (!drop) {
        this.tryExitDrop(e);
      }
    }
  }

  private tryEnterDrop(e: TrgDrag.DragEvent, drop: TrgDropDirective) {
    try {
      this.ngZone.run(() => {
        drop.onDropEnter(e);
        this._drop = drop;
        this.enterDrop.emit({ ...e, drop });
      });

      return true;
    }
    catch (err) {
      LOGGER.error(err);
      return false;
    }
  }

  private tryExitDrop(e: TrgDrag.DragEvent) {
    this.preview.element.classList.remove('trg-drag-preview-dropping');

    if (this._drop) {
      this.ngZone.run(() => {
        let drop = this._drop;
        this._drop = null;

        if (!drop.destroyed) {
          try {
            drop.onDropExit(e);
            return true;
          }
          catch (err) {
            LOGGER.error(err);
            return false;
          }
          finally {
            this.exitDrop.emit({ ...e, drop });
          }
        }
      });
    }
  }

  private hookEvent(target: any, eventName: string, callback: (e: TrgDrag.DragEvent) => void) {
    return this.ngZone.runOutsideAngular(() => {
      this._subs.add(
        fromEvent(target, eventName).pipe(
          map((mouseEvent: MouseEvent) => this.createDragEvent(mouseEvent)),
          filter((e: TrgDrag.DragEvent) => {
            if (!this.dragging())
              return false;

            return this.eventAllowed(e);
          }),
          tap((e: TrgDrag.DragEvent) => {
            e = {
              ...e,
              preview: this.preview
            };

            callback(e);
          }),

          takeUntilDestroyed(this.destroyRef),
        ).subscribe());
    });
  }

  private eventAllowed(e: TrgDrag.DragEvent) {
    let buttons = this.buttons();
    if (buttons && !buttons.includes(e.mouseEvent.button))
      return false;

    let filter = this.eventFilter();
    if (filter && !filter(e))
      return false;

    return true;
  }

  private createDragEvent(mouseEvent: MouseEvent): TrgDrag.DragEvent {
    return {
      drag: this,
      mouseEvent,
      preview: null,

      data: this.data(),

      startX: this._start.x,
      startY: this._start.y,

      x: mouseEvent.pageX,
      y: mouseEvent.pageY,

      elementX: mouseEvent.pageX + this._offset.x,
      elementY: mouseEvent.pageY + this._offset.y,

      deltaX: mouseEvent.pageX - this._last.x,
      deltaY: mouseEvent.pageY - this._last.y,
    };
  }

  private canDrag(starting: boolean) {
    if (this.destroyed)
      return false;

    if (this.dragDrop.dragDisabled() || this.dragDisabled()) {
      return false;
    }

    let activeDrag = this.dragDrop.drag();

    if (starting) {
      if (activeDrag) {
        return false;
      }

      if (this.dragging()) {
        LOGGER.warn(`Already started dragging.`, this);
        return false;
      }
    }
    else {
      if (activeDrag !== this) {
        LOGGER.warn(`Active drag changed.`, this);
        return false;
      }

      if (!this.dragging()) {
        LOGGER.warn(`Dragging stopped abruptly.`, this);
        return false;
      }
    }

    return true;
  }

  canDropTo(drop: TrgDropDirective) {
    let allowedTags = this.dropTags();
    if (isNullOrUndefined(allowedTags))
      return true;

    if (typeof allowedTags === 'string')
      allowedTags = [allowedTags];

    let dropTags = drop.dropTags();
    if (isNullOrUndefined(dropTags))
      return false;

    if (typeof dropTags === 'string')
      dropTags = [dropTags];

    return dropTags.some(t => allowedTags.some(x => x == t));
  }
};

@Directive({
  selector: '[trgDragHandle]',
  exportAs: 'TrgDragHandleDirective',
  standalone: false
})
export class TrgDragHandleDirective {
  readonly dragDirective = input<TrgDragDirective>(null);
  readonly dragDisabled = input(false);

  @HostListener('mousedown', ['$event'])
  onMouseDown(mouseEvent: MouseEvent) {
    if (this.dragDisabled())
      return;

    this.dragDirective()?.onMouseDown(mouseEvent, true);
  }
}

export namespace TrgDrag {
  export class DragPreview {
    /** When true - the preview element to be kept after drag ends.
     * Otherwise the element will be removed. */
    public keep = false;

    public _view: EmbeddedViewRef<any>;

    constructor(
      public readonly drag: TrgDragDirective,
      public readonly element: HTMLElement) { }

    destroy() {
      try {
        this._view?.destroy();
      }
      finally {
        this._view = null;

        try {
          this.element?.remove();
        }
        finally {
          this.drag?._previewDestroyed(this);
        }
      }
    }

    transitionTo(options: {
      x: number;
      y: number;
      destoryAfter?: boolean;
      maxWaitTime?: number;
      duration?: number;
    }) {
      let { x, y, destoryAfter, maxWaitTime, duration } = options;

      this.keep = !destoryAfter;

      if (!isNullOrUndefined(duration))
        this.element.style.setProperty('transition-duration', `${duration}ms`);

      this.element.style.setProperty('left', `${x}px`);
      this.element.style.setProperty('top', `${y}px`);

      let transitionEvent = race(
        fromEvent(this.element, 'transitionend'),
        fromEvent(this.element, 'transitioncancel')
      ).pipe(delay(0));

      let source = maxWaitTime
        ? race(transitionEvent, timer(maxWaitTime))
        : transitionEvent;

      return source.pipe(
        take(1),
        switchMap(() => timer(0).pipe(
          tap(() => {
            this.element.style.removeProperty('transition-duration');

            if (destoryAfter)
              this.destroy();
          })
        ))
      );
    }
  };

  export interface DragEvent {
    readonly drag: TrgDragDirective;
    readonly mouseEvent: MouseEvent;
    readonly preview: DragPreview;

    readonly data: any;

    readonly startX: number;
    readonly startY: number;

    readonly x: number;
    readonly y: number;

    readonly elementX: number;
    readonly elementY: number;

    readonly deltaX: number;
    readonly deltaY: number;
  }

  export enum Buttons {
    Primary = 0,
    Secondary = 1,
  }

  export type EventFilter = (event: TrgDrag.DragEvent) => boolean;

  export type DropPredicate = (e: TrgDrag.DragEvent, drop: TrgDropDirective) => boolean;

  export type DragPreviewSizeValue = { width: number | string; height: number | string };

  export type DragPreviewSizeInput = DragPreviewSizeValue | (() => DragPreviewSizeValue) | HTMLElement;
}
